import React, { useState, useEffect } from 'react';
import type { ReactNode } from 'react';

import { QuestionCircleOutlined, EyeOutlined, EyeInvisibleOutlined, ShoppingOutlined } from '@ant-design/icons';
import ProductSelect from '@components/shared/products/ProductSelect';
import PaperSizeFormItems from '@components/shared/templates/PaperSizeFormItems';
import TemplatePreviewModal from '@components/shared/templates/TemplatePreviewModal';
import TemplatePreviewSplitPane from '@components/shared/templates/TemplatePreviewSplitPane';
import FloatingActionBar, { FloatingActionBarSpacer } from '@components/shared/ui/FloatingActionBar';
import useSmartBack from '@hooks/shared/useSmartBack';
import {
  handleApiError as handleApiErrorRaw,
  handleNetworkError as handleNetworkErrorRaw,
} from '@utils/apiErrorHandler';
// apiErrorHandler 是 .jsx，JSDoc 把 options 推成 required，這裡 cast 為 wider 型別
// 對齊既有 .tsx callers (BrandShow / BrandEdit / ShelfShowView) 的慣例。
const handleApiError = handleApiErrorRaw as (response: unknown, options?: Record<string, unknown>) => void;
const handleNetworkError = handleNetworkErrorRaw as (error: unknown, options?: Record<string, unknown>) => void;
import {
  Form,
  Input,
  Button,
  Card,
  message,
  Space,
  Checkbox,
  Spin,
  Row,
  Col,
  Switch
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import adminProductService from '@services/admin/productService';

const { TextArea } = Input;

// Paper size types for product labels
const PAPER_SIZE_TYPES: Record<string, { width: number | null; height: number | null; labelKey: string }> = {
  label_small: { width: 40, height: 30, labelKey: 'enums.product_label_paper_size.label_small' },
  label_medium: { width: 70, height: 50, labelKey: 'enums.product_label_paper_size.label_medium' },
  label_large: { width: 100, height: 70, labelKey: 'enums.product_label_paper_size.label_large' },
  a4: { width: 210, height: 297, labelKey: 'enums.product_label_paper_size.a4' },
  a5: { width: 148, height: 210, labelKey: 'enums.product_label_paper_size.a5' },
  custom: { width: null, height: null, labelKey: 'enums.product_label_paper_size.custom' }
};

const getPaperSizeOptions = (t: (key: string) => string) => {
  return Object.entries(PAPER_SIZE_TYPES).map(([key, value]) => ({
    label: t(value.labelKey),
    value: key
  }));
};

const MM_TO_PX = 3.7795; // 1mm ≈ 3.7795px at 96 DPI

const getActualPaperSize = (
  paperSizeType: string | undefined,
  paperWidth: number | undefined,
  paperHeight: number | undefined
) => {
  let widthMm: number;
  let heightMm: number;

  if (paperSizeType === 'custom') {
    widthMm = paperWidth || 100;
    heightMm = paperHeight || 70;
  } else {
    widthMm = (paperSizeType && PAPER_SIZE_TYPES[paperSizeType]?.width) || 100;
    heightMm = (paperSizeType && PAPER_SIZE_TYPES[paperSizeType]?.height) || 70;
  }

  return {
    width: widthMm,
    height: heightMm,
    widthPx: Math.round(widthMm * MM_TO_PX),
    heightPx: Math.round(heightMm * MM_TO_PX)
  };
};

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  [key: string]: unknown;
}

interface TemplateLoadedPayload {
  merchant_id?: string | null;
  merchant?: { id?: string | null; name?: string | null } | null;
  [key: string]: unknown;
}

interface ProductLabelTemplatesEditProps {
  role?: 'admin' | 'operator';
  getById: (id: string) => Promise<ApiResponse<Record<string, unknown>>>;
  create: (values: Record<string, unknown>) => Promise<ApiResponse<{ id?: string }>>;
  update: (id: string, values: Record<string, unknown>) => Promise<ApiResponse<{ id?: string }>>;
  getDefaultTemplate: (locale: string) => Promise<ApiResponse<{ content?: string }>>;
  // merchantIdForFilters is record-first: wrapper passes the loaded record's merchant_id
  // in edit mode, or the operator's currentMerchant.id in new mode. Shared is
  // merchant-agnostic and never reads OperatorDataContext directly.
  merchantIdForFilters?: string | null;
  // Called by `loadData` once the template is fetched. Wrapper uses this to sync
  // OperatorDataContext.currentMerchant to the record's merchant before any
  // merchant-scoped dropdown (ProductSelect) renders.
  onTemplateLoaded?: (template: TemplateLoadedPayload) => void;
  // True while the wrapper is still switching context to the record's merchant.
  // Render-gated to avoid the form briefly showing the previous merchant's options.
  merchantDataLoading?: boolean;
  productService?: unknown; // 可選，未傳入時使用 adminProductService
}

/**
 * ProductLabelTemplatesEdit - Product Label Template edit/create form component
 *
 * @param {Object} props
 * @param {string} props.role - Role (admin or operator)
 * @param {Function} props.getById - Get template by ID function
 * @param {Function} props.create - Create template function
 * @param {Function} props.update - Update template function
 * @param {Function} props.getDefaultTemplate - Get default template function
 * @param {string} props.merchantIdForFilters - Record-aligned merchant id for ProductSelect filter
 * @param {Function} props.onTemplateLoaded - Wrapper hook to sync OperatorDataContext to record's merchant
 * @param {boolean} props.merchantDataLoading - True while wrapper is still aligning context
 */
const ProductLabelTemplatesEdit: React.FC<ProductLabelTemplatesEditProps> = ({
  role = 'admin',
  getById,
  create,
  update,
  getDefaultTemplate,
  merchantIdForFilters = null,
  onTemplateLoaded,
  merchantDataLoading = false,
  productService = null // 可選，未傳入時使用 adminProductService
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const goBack = useSmartBack();
  const { id } = useParams<{ id?: string }>();
  const isNew = !id;
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  // 區分「儲存並新增下一筆」的 loading
  const [savingAndAdding, setSavingAndAdding] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [renderError, setRenderError] = useState<unknown>(null);

  // Split preview state (controlled by shared component)
  const [splitPreviewVisible, setSplitPreviewVisible] = useState(true);

  // Product selection state
  const [loadedProductData, setLoadedProductData] = useState<Record<string, any> | null>(null);

  // Default sample product data (matching actual Product model attributes)
  const defaultSampleProductData = {
    product: {
      sku: "SKU-001",
      name: "經典純棉T恤",
      barcode: "4712345678901",
      size: "M",
      color: "藍色",
      category: "服飾",
      spec: "100% 純棉，透氣舒適",
      price: 580.0,
      material: "100% 純棉",
      manufacturer_country: "台灣",
      hs_code: "6109.10.00",
      temperature: "ambient",
      note: "建議手洗或使用洗衣袋機洗，避免高溫烘乾。限量款式，售完為止。",
      created_at: "2025-01-03 10:30:00"
    },
    brand: {
      name: "經典品牌"
    },
    merchant: {
      name: "測試貨主",
      logo: { url: null }
    }
  };

  // Use loaded product data if available, otherwise use default sample
  const sampleProductData = loadedProductData || defaultSampleProductData;

  // 使用傳入的 productService，若未傳入則使用 adminProductService
  const effectiveProductService = productService || adminProductService;

  // Watch form fields for preview
  const contentValue = Form.useWatch('content', form);
  const paperSizeType = Form.useWatch('paper_size_type', form);
  const paperWidth = Form.useWatch('paper_width', form);
  const paperHeight = Form.useWatch('paper_height', form);

  // Calculate actual paper size
  const actualPaperSize = getActualPaperSize(paperSizeType, paperWidth, paperHeight);

  // Record-first: wrapper passes either the loaded record's merchant_id (edit)
  // or the operator's currentMerchant.id (new). Shared component is merchant-agnostic.
  const targetMerchantId = merchantIdForFilters;

  // Handle product selection
  const handleProductSelect = (productId: string, product: any) => {
    if (!product) return;
    // Transform product data to match template format
    // ⚠️ IMPORTANT: Keep null/undefined values as-is for Liquid {% if %} conditions
    // This ensures preview matches PDF output (empty strings would make {% if %} true)
    const transformedData = {
      product: {
        sku: product.sku || null,
        name: product.name || null,
        barcode: product.barcode || null,
        category: product.product_category?.name || null,
        price: product.price ?? 0,
        material: product.material || null,
        manufacturer_country: product.manufacturer_country || null,
        hs_code: product.hs_code || null,
        temperature: product.temperature || 'ambient',
        note: product.note || null,
        created_at: product.created_at ? product.created_at.split('T')[0].replace(/-/g, '/') : null,
        ...(product.custom_attributes || {}),
      },
      brand: product.brand || product.brand_name ? {
        name: product.brand?.name || product.brand_name || null
      } : null,
      merchant: {
        name: product.merchant?.name || product.merchant_name || null,
        logo: product.merchant?.logo || { url: null }
      }
    };

    setLoadedProductData(transformedData);
    message.success(t('shared.product_label_templates.form.product_loaded', { name: product.name }));
  };

  // Reset to default sample data
  const handleResetSampleData = () => {
    setLoadedProductData(null);
    message.info(t('shared.product_label_templates.form.sample_reset'));
  };

  useEffect(() => {
    loadData();
  }, [id, i18n.language]);

  // 新增模式：載入預設模板內容並套用到 form（抽成 helper 以便重置後重新套用）
  const applyDefaultsForNew = async () => {
    try {
      const locale = i18n.language || 'zh';
      const defaultTemplateResponse = await getDefaultTemplate(locale);
      if (defaultTemplateResponse.success) {
        form.setFieldsValue({
          content: defaultTemplateResponse.data?.content,
          is_default: false,
          paper_size_type: 'label_medium',
        });
      } else {
        // Fallback to empty content if fetching default template fails
        form.setFieldsValue({
          content: '',
          is_default: false,
          paper_size_type: 'label_medium',
        });
      }
    } catch (error) {
      console.error('Failed to load default template:', error);
      // Fallback to empty content
      form.setFieldsValue({
        content: '',
        is_default: false,
        paper_size_type: 'label_medium',
      });
    }
  };

  const loadData = async () => {
    try {
      setDataLoading(true);

      // Load product label template data if editing
      if (id) {
        const response = await getById(id);
        if (response.success) {
          const template = (response.data || {}) as Record<string, any>;

          // 通知 wrapper：拿到 record 後讓它把 OperatorDataContext.currentMerchant
          // 對齊到該 record 的 merchant（由 useRecordMerchantSync 處理）。shared
          // 本身不再讀 / 寫 merchant context，保持 merchant-agnostic。
          onTemplateLoaded?.(template);

          form.setFieldsValue({
            name: template.name,
            content: template.content,
            is_default: template.is_default,
            description: template.description,
            paper_size_type: template.paper_size_type || 'label_medium',
            paper_width: template.paper_width,
            paper_height: template.paper_height,
          });
        } else {
          handleApiError(response, { t, fallbackMessage: t('common.error.record_not_found') });
          navigate('/product_label_templates');
        }
      } else {
        // New template: fetch and initialize with default template
        await applyDefaultsForNew();
      }
    } catch (error) {
      handleNetworkError(error, { t });
      console.error('Load data error:', error);
    } finally {
      setDataLoading(false);
    }
  };

  // 預覽模板
  const handlePreview = () => {
    const content = form.getFieldValue('content');

    if (!content) {
      message.warning(t('common.validation.required'));
      return;
    }

    setPreviewVisible(true);
  };

  // 組出 API payload
  const buildProcessedValues = (values: any) => ({
    name: values.name,
    content: values.content,
    is_default: values.is_default || false,
    description: values.description,
    paper_size_type: values.paper_size_type || 'label_medium',
    paper_width: values.paper_size_type === 'custom' ? values.paper_width : null,
    paper_height: values.paper_size_type === 'custom' ? values.paper_height : null,
  });

  // 核心提交邏輯：呼叫 create/update 並回傳 response
  const submitValues = async (processedValues: Record<string, unknown>) => {
    return id
      ? await update(id, processedValues)
      : await create(processedValues);
  };

  // 主要提交：成功後導向 show 頁
  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      const processedValues = buildProcessedValues(values);
      const response = await submitValues(processedValues);

      if (response.success) {
        message.success(t(id ? `common.message.save_success` : `common.message.save_success`));
        // 導向該筆記錄的 show 頁
        const recordId = response.data?.id || id;
        navigate(`/product_label_templates/${recordId}`, { replace: true });
      } else {
        handleApiError(response, {
          t,
          fallbackMessage: t(id ? `common.error.save_failed` : `common.error.save_failed`)
        });
      }
    } catch (error) {
      handleNetworkError(error, { t });
      console.error('Submit error:', error);
    } finally {
      setLoading(false);
    }
  };

  // 儲存並新增下一筆：僅在新建模式可用，成功後重置表單 + 重新載入預設模板
  const handleSaveAndAddAnother = async () => {
    try {
      const values = await form.validateFields();
      setSavingAndAdding(true);
      try {
        const processedValues = buildProcessedValues(values);
        const response = await submitValues(processedValues);

        if (response.success) {
          message.success(t('common.message.save_success'));
          // 重置 antd form 與 loaded product data，並重新套用預設模板
          form.resetFields();
          setLoadedProductData(null);
          await applyDefaultsForNew();
        } else {
          handleApiError(response, {
            t,
            fallbackMessage: t('common.error.save_failed')
          });
        }
      } catch (error) {
        handleNetworkError(error, { t });
        console.error('Submit error:', error);
      } finally {
        setSavingAndAdding(false);
      }
    } catch {
      // validateFields 失敗會 reject，antd 會自動高亮錯誤欄位
    }
  };

  // merchantDataLoading 由 wrapper 透過 useRecordMerchantSync 傳入：在 edit 模式
  // 下從拿到 record.merchant_id 到 OperatorDataContext.currentMerchant 對齊的這
  // 段期間是 true。在期間擋住 form 渲染避免閃出別的 merchant 的下拉清單。
  if (dataLoading || merchantDataLoading) {
    return (
      <div style={{ textAlign: 'center', paddingTop: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Space align="center">
          {splitPreviewVisible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
          <span>{t('shared.product_label_templates.form.split_preview')}</span>
          <Switch
            checked={splitPreviewVisible}
            onChange={setSplitPreviewVisible}
          />
        </Space>
      </div>

      <Row gutter={24}>
        {/* Left: Form */}
        <Col span={splitPreviewVisible ? 14 : 24}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            autoComplete="off"
          >
              <Form.Item
                label={t(`models.product_label_template.name`)}
                name="name"
                rules={[{ required: true, message: t(`common.validation.required`) }]}
              >
                <Input placeholder={t(`common.placeholder.please_enter`)} />
              </Form.Item>

              {/* Paper Size Form Items (Shared Component) */}
              <PaperSizeFormItems
                paperSizeOptions={getPaperSizeOptions(t)}
                showCustomInputs={paperSizeType === 'custom'}
                t={t}
                modelName="product_label_template"
                namespace="shared.product_label_templates"
              />

              {/* Load Product Data Section */}
              <Card
                type="inner"
                size="small"
                title={t('shared.product_label_templates.form.preview_data_title')}
                style={{ marginBottom: 24 }}
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>
                      {loadedProductData ? (
                        <>
                          <strong>{t('shared.product_label_templates.form.current_product')}:</strong>{' '}
                          {loadedProductData.product.name} ({loadedProductData.product.sku})
                        </>
                      ) : (
                        <span style={{ color: '#999' }}>
                          {t('shared.product_label_templates.form.using_sample_data')}
                        </span>
                      )}
                    </span>
                    <Space>
                      <ProductSelect
                        productService={effectiveProductService}
                        merchantId={targetMerchantId}
                        placeholder={t('shared.product_label_templates.form.load_product')}
                        onSelect={handleProductSelect}
                        value={null}
                        style={{ width: 250 }}
                      />
                      {loadedProductData && (
                        <Button
                          onClick={handleResetSampleData}
                        >
                          {t('shared.product_label_templates.form.reset_sample')}
                        </Button>
                      )}
                    </Space>
                  </div>
                </Space>
              </Card>

              <Form.Item
                label={
                  <Space>
                    {t(`models.product_label_template.content`)}
                    <Button
                      type="link"
                      size="small"
                      icon={<QuestionCircleOutlined />}
                      href="https://shopify.github.io/liquid/"
                      target="_blank"
                    >
                      {t('shared.product_label_templates.form.view_liquid_docs')}
                    </Button>
                  </Space>
                }
                name="content"
                rules={[{ required: true, message: t(`common.validation.required`) }]}
              >
                <TextArea
                  rows={20}
                  placeholder={t(`common.placeholder.please_enter`)}
                  style={{ fontFamily: 'monospace', fontSize: '13px' }}
                />
              </Form.Item>

              {/* 預覽按鈕 */}
              <Form.Item>
                <Button
                  icon={<EyeOutlined />}
                  onClick={handlePreview}
                  style={{ marginBottom: '16px' }}
                >
                  {t('shared.product_label_templates.form.preview')}
                </Button>
              </Form.Item>

              <Form.Item
                name="is_default"
                valuePropName="checked"
              >
                <Checkbox>
                  {t(`models.product_label_template.is_default`)}
                </Checkbox>
              </Form.Item>

              <Form.Item
                label={t(`models.product_label_template.description`)}
                name="description"
              >
                <TextArea
                  rows={3}
                  placeholder={t(`common.placeholder.please_enter`)}
                />
              </Form.Item>

              <FloatingActionBarSpacer />
            </Form>
          </Col>

          {/* Right: Split Preview (Shared Component) */}
          {splitPreviewVisible && (
            <Col span={10}>
              <TemplatePreviewSplitPane
                visible={splitPreviewVisible}
                content={contentValue}
                sampleData={sampleProductData}
                paperSize={actualPaperSize}
                cssVariablePrefix="label"
                t={t}
                namespace="shared.product_label_templates"
                scale={0.85}
                onRenderError={setRenderError}
              />
            </Col>
          )}
        </Row>

      <FloatingActionBar
        role={role}
        onCancel={goBack}
        onSubmit={() => form.submit()}
        loading={loading}
        disabled={!!renderError || savingAndAdding}
        extraButtons={isNew ? (
          <Button
            loading={savingAndAdding}
            disabled={loading || !!renderError}
            onClick={handleSaveAndAddAnother}
          >
            {t('common.button.save_and_add_another')}
          </Button>
        ) : null}
      />

      {/* Preview Modal (Shared Component) */}
      <TemplatePreviewModal
        visible={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        content={contentValue}
        sampleData={sampleProductData}
        paperSize={actualPaperSize}
        cssVariablePrefix="label"
        t={t}
        namespace="shared.product_label_templates"
      />
    </div>
  );
};

export default ProductLabelTemplatesEdit;
